export type CookiePreferences = {
    necessary: true;
    analytics: boolean;
    targeting: boolean;
};

const COOKIE_NAME = "cc_cookie";
// 182 days, matching the previous vanilla-cookieconsent library's default consent expiry.
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 182;

// consentId/timestamps are only read by the legacy vanilla-cookieconsent library (see buildCookieValue).
type ConsentCookieValue = {
    categories: string[];
    consentId?: string;
    consentTimestamp?: string;
    lastConsentTimestamp?: string;
};

function toPreferences(value: ConsentCookieValue | null | undefined): CookiePreferences | null {
    if (!value?.categories) return null;

    return {
        necessary: true,
        analytics: value.categories.includes("analytics"),
        targeting: value.categories.includes("targeting"),
    };
}

function toCategories(preferences: CookiePreferences): string[] {
    const categories = ["necessary"];
    if (preferences.analytics) categories.push("analytics");
    if (preferences.targeting) categories.push("targeting");
    return categories;
}

// Just needs to be a non-empty string (not necessarily a real UUID); avoids crypto.randomUUID(),
// which the older browsers nuxt-vite-legacy's legacy bundle serves don't support.
function generateConsentId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Web/Website's vanilla-cookieconsent treats cc_cookie as invalid without these fields, re-showing its banner (UT-10010).
function buildCookieValue(preferences: CookiePreferences, existing: ConsentCookieValue | null): ConsentCookieValue {
    const now = new Date().toISOString();

    return {
        ...(existing ?? {}),
        categories: toCategories(preferences),
        consentId: existing?.consentId ?? generateConsentId(),
        consentTimestamp: existing?.consentTimestamp ?? now,
        lastConsentTimestamp: now,
    };
}

/**
 * TrustedParts-owned cookie consent manager (replaces vanilla-cookieconsent, see UT-10015).
 *
 * State (preferences/banner/dialog visibility) is shared across every component via useState.
 * Only app.vue calls initialize(), once, from onMounted — everything else (the banner, the
 * preferences dialog, cookie-policy.vue) just reads the returned state/actions.
 */
export function useCookieConsent() {
    const { gtag, initialize: initializeGtag } = useGtag();
    const rollbar = useRollbar();
    const { $api } = useNuxtApp();
    const route = useRoute();
    const requestUrl = useRequestURL();
    const isBot = useStateIsBot();

    const cookie = useCookie<ConsentCookieValue | null>(COOKIE_NAME, {
        path: "/",
        sameSite: "lax",
        secure: requestUrl.protocol === "https:",
        maxAge: COOKIE_MAX_AGE_SECONDS,
        default: () => null,
    });

    // Resolved from the cookie on first call, server or client, so preferences are already
    // correct by the time any component (including during SSR) reads them — no separate "load"
    // step needed.
    const preferences = useState<CookiePreferences | null>("cookieConsentPreferences", () =>
        toPreferences(cookie.value)
    );
    const isBannerVisible = useState("cookieConsentBannerVisible", () => false);
    const isDialogVisible = useState("cookieConsentDialogVisible", () => false);

    async function logConsent(current: CookiePreferences) {
        try {
            const response = await $api<{ Guid: string }>("cookie-consent", {
                method: "POST",
                body: {
                    PerformanceAndAnalytics: current.analytics,
                    TargetingAndAdvertising: current.targeting,
                },
            });

            if (response.Guid) {
                localStorage.setItem("trusted-parts-cookie-consent-accepted-on", new Date().getTime().toString());
                localStorage.setItem("trusted-parts-cookie-consent-accepted-guid", response.Guid);
            }
        } catch (e) {
            rollbar.error("Cookie Consent: failed to set preferences", e as Error);
        }
    }

    async function persist(current: CookiePreferences) {
        cookie.value = buildCookieValue(current, cookie.value);
        preferences.value = current;
        isBannerVisible.value = false;
        isDialogVisible.value = false;

        await logConsent(current);

        // Reload so server-rendered content and any client-loaded tags (Multiview, Google tags)
        // restart from the newly recorded consent state instead of trying to reconcile in place.
        reloadNuxtApp({ force: true });
    }

    return {
        preferences,
        isBannerVisible,
        isDialogVisible,

        // Client-only side effects (gtag script injection, banner auto-show); called exactly
        // once, from app.vue's onMounted.
        initialize() {
            const current = preferences.value ?? { necessary: true, analytics: false, targeting: false };

            // Google Consent Mode: only load gtag.js once an optional category has been accepted
            // (see nuxt.config.ts `gtag.initMode: "manual"`). Loading it unconditionally — even
            // with every storage type denied — still lets GA4 send cookieless "modeled" pings,
            // which is not opt-in per UT-10013.
            if (current.analytics || current.targeting) {
                initializeGtag();
            }
            gtag("consent", "update", {
                analytics_storage: current.analytics ? "granted" : "denied",
                ad_storage: current.targeting ? "granted" : "denied",
                ad_user_data: current.targeting ? "granted" : "denied",
                ad_personalization: current.targeting ? "granted" : "denied",
            });

            if (isBot.value) return;

            const forceShow = route.query["load-cookie-consent"] !== undefined;
            if (forceShow || !preferences.value) {
                isBannerVisible.value = true;
            }
        },

        acceptAll() {
            return persist({ necessary: true, analytics: true, targeting: true });
        },

        acceptEssential() {
            return persist({ necessary: true, analytics: false, targeting: false });
        },

        savePreferences(next: Omit<CookiePreferences, "necessary">) {
            return persist({ necessary: true, ...next });
        },
    };
}
